/**
 * Verificarea structurală după schema comună. Interpretează același subset de JSON Schema
 * ca `physics/src/schema.rs`, cu aceleași coduri și căi, fără generare de cod (merge și sub CSP strict).
 */
import type { Issue } from './types';

type Json = unknown;
type Schema = Record<string, unknown>;

const join = (path: string, key: string) => (path ? `${path}.${key}` : key);

function typeName(v: Json): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  return typeof v === 'object' ? 'object' : typeof v;
}

function typeOk(v: Json, t: string): boolean {
  switch (t) {
    case 'object':
      return typeName(v) === 'object';
    case 'array':
      return Array.isArray(v);
    case 'string':
      return typeof v === 'string';
    case 'boolean':
      return typeof v === 'boolean';
    case 'number':
      return typeof v === 'number' && Number.isFinite(v);
    case 'integer':
      return typeof v === 'number' && Number.isInteger(v);
    default:
      return true;
  }
}

function equal(a: Json, b: Json): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const err = (code: string, path: string, message: string): Issue => ({ severity: 'error', code, path, message });

export function checkSchema(schema: Schema, v: Json, path: string, out: Issue[]): void {
  if ('const' in schema) {
    if (!equal(v, schema.const)) out.push(err('schema.const', path, `trebuie să fie ${JSON.stringify(schema.const)}`));
    return;
  }
  const t = schema.type as string | undefined;
  if (t && !typeOk(v, t)) {
    out.push(err('schema.type', path, `tip greșit: așteptat ${t}, găsit ${typeName(v)}`));
    return;
  }
  const en = schema.enum as Json[] | undefined;
  if (en && !en.some((x) => equal(x, v))) out.push(err('schema.enum', path, `valoare nepermisă ${JSON.stringify(v)}; permise: ${en.map((x) => JSON.stringify(x)).join(', ')}`));
  if (typeof schema.pattern === 'string' && typeof v === 'string' && !new RegExp(schema.pattern).test(v)) out.push(err('schema.pattern', path, `„${v}” nu respectă formatul ${schema.pattern}`));
  if (typeof v === 'number') {
    if (typeof schema.minimum === 'number' && v < schema.minimum) out.push(err('schema.range', path, `${v} e sub minimul realist ${schema.minimum}`));
    if (typeof schema.maximum === 'number' && v > schema.maximum) out.push(err('schema.range', path, `${v} depășește maximul realist ${schema.maximum}`));
  }
  if (Array.isArray(v)) {
    if (typeof schema.minItems === 'number' && v.length < schema.minItems) out.push(err('schema.items', path, `are ${v.length} elemente, minimum ${schema.minItems}`));
    if (typeof schema.maxItems === 'number' && v.length > schema.maxItems) out.push(err('schema.items', path, `are ${v.length} elemente, maximum ${schema.maxItems}`));
    if (schema.items) v.forEach((it, i) => checkSchema(schema.items as Schema, it, join(path, String(i)), out));
  }
  if (typeName(v) === 'object') {
    const obj = v as Record<string, Json>;
    const props = (schema.properties ?? {}) as Record<string, Schema>;
    for (const r of (schema.required as string[] | undefined) ?? []) if (!(r in obj)) out.push(err('schema.required', join(path, r), 'câmp obligatoriu lipsă'));
    const closed = schema.additionalProperties === false;
    for (const [k, val] of Object.entries(obj)) {
      if (props[k]) checkSchema(props[k], val, join(path, k), out);
      else if (closed) out.push(err('schema.unknown', join(path, k), 'câmp necunoscut (greșeală de scriere?)'));
    }
  }
}
